import React, { useEffect, useState } from "react";
import { IG_STEP, stepOrder, useOnboardingState, visibleStep } from "../../hooks/useOnboardingState";
import OnboardingProgressBar from "../../components/OnboardingProgressBar";
import Step1Welcome from "./steps/Step1Welcome";
import Step2Identity from "./steps/Step2Identity";
import StepConnectInstagram from "./steps/StepConnectInstagram";
import Step6Complete from "./steps/Step6Complete";
// Phase C: the price step (old Step3Profile) is gone — the DJ sets their
// price in the first-sign-in popup, in their real currency. 1.5.1 #1 folded
// the second profile screen (old Step4Enhancements) into Step2Identity. File
// names keep their historic numbers; the switch below is the actual order.
import { selectPath, track, trackOnce } from "../../lib/analytics";
import "./OnboardingPage.css";
import "./OnboardingShell.css";
import { t } from "../../i18n";

// Growth funnel step labels (internal step numbers — see renderStep).
const STEP_NAMES: Record<number, string> = {
  1: "sign_in",
  2: "profile",
  3: "connect_bank",
  [IG_STEP]: "connect_instagram",
};

export default function OnboardingPage() {
  const { step, data, updateData, nextStep, prevStep, goToStep, clearState, clearPersistedStorage, saveBeforeLeaving } = useOnboardingState();

  // Every step renders into the same scrollable page, so advancing while
  // scrolled down would drop the user mid-page on the next step — below the
  // progress bar. Reset scroll to the top whenever the step changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Growth funnel: arriving here on web IS choosing the DJ path.
  useEffect(() => {
    selectPath("dj", "signup_page");
    trackOnce("signup_started", "signup");
  }, []);

  const trackedNext = () => {
    track("signup_step_completed", { props: { step, step_name: STEP_NAMES[step] || String(step) } });
    nextStep();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1Welcome
            data={data}
            updateData={updateData}
            onNext={trackedNext}
          />
        );
      case 2:
        return (
          <Step2Identity
            data={data}
            updateData={updateData}
            onNext={trackedNext}
            onBack={prevStep}
          />
        );
      case IG_STEP:
        // Connect Instagram, its own full screen in the fans' look (build 54):
        // the step strip rides under its wordmark, not in the shell.
        return (
          <StepConnectInstagram
            data={data}
            updateData={updateData}
            onNext={trackedNext}
            onBack={prevStep}
            saveBeforeLeaving={saveBeforeLeaving}
            strip={<OnboardingProgressBar currentStep={visible.current} totalSteps={visible.total} withInstagram />}
          />
        );
      case 3:
        return (
          <Step6Complete
            data={data}
            updateData={updateData}
            clearState={clearState}
            clearPersistedStorage={clearPersistedStorage}
            goToStep={goToStep}
            onBack={prevStep}
          />
        );
      default:
        goToStep(1);
        return null;
    }
  };

  // The profile and bank steps share one shell (OnboardingShell.css): the
  // green glow, the wordmark header with the step dots, and one button spot
  // at the bottom. The sign-in screen (internal step 1) keeps its own look,
  // and so does Connect Instagram (the fans' stage, build 54).
  const inShell = step === 2 || step === 3;
  const fullScreen = inShell || step === IG_STEP;
  // "Step N of M": 2 steps, or 3 with Connect Instagram.
  const visible = visibleStep(step, data.igStep);
  const [wordmarkBroken, setWordmarkBroken] = useState(false);

  // The shell carries its own header, so the site header and footer step
  // aside while it shows. A body class (not a Layout prop): Layout doesn't
  // know which signup step is on screen. The site header measures itself
  // (--app-header-h), so hiding it also gives the page its space back.
  useEffect(() => {
    if (!fullScreen) return;
    document.body.classList.add("ob-shell-active");
    return () => document.body.classList.remove("ob-shell-active");
  }, [fullScreen]);

  return (
    <div className={`onboarding-page${inShell ? " ob-shell" : ""}`}>
      <div className="onboarding-container">
        {inShell && (
          <div className="ob-header">
            <button type="button" className="ob-header-back" onClick={prevStep} aria-label={t("onboardingPage.goBackToPrevious")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            {/* Never a broken-image icon: if the file can't load, the name is set in type. */}
            {wordmarkBroken ? (
              <span className="ob-header-wordmark-text">{t("onboardingPage.request")}</span>
            ) : (
              <img className="ob-header-wordmark" src="/request-wordmark.png" alt={t("onboardingPage.request")} onError={() => setWordmarkBroken(true)} />
            )}
            <div className="ob-header-dots" aria-hidden="true">
              {stepOrder(data.igStep).slice(1).map((n) => <span key={n} className={n === step ? "on" : undefined} />)}
            </div>
          </div>
        )}
        {/* 2-step progress bar (Phase C dropped the price step; build 41
            dropped the access-code interstitial; 1.5.1 #1 merged the two
            profile screens). Shows on the profile step (internal 2) and the
            final "Connect bank" step (internal 3). Internal step 1 (sign-in)
            shows no bar. Displayed numbering: internal 2 = step 1 of 2,
            internal 3 = 2 of 2 — or, with Connect Instagram (internal 4,
            build 54), profile 1 of 3, Instagram 2 of 3, bank 3 of 3. */}
        {inShell ? (
          <OnboardingProgressBar currentStep={visible.current} totalSteps={visible.total} withInstagram={data.igStep} />
        ) : null}

        {renderStep()}
      </div>
    </div>
  );
}
